// ═══════════════════════════════════════════════════
//  Tufting wire hardware, resolved in exactly one place
//
//  The sibling of button-hardware.ts, and deliberately a sibling rather than a
//  generalization of it. The two share a SHAPE — id, else default, else
//  nothing; three warnings for a quantity that cannot be charged — but not a
//  rule: wire is a float count of linear feet gated on is_tufted, buttons are
//  an integer count gated on has_buttons. Folding them together would rewrite
//  eight call sites that started charging correctly one day earlier. See spec
//  2026-09-03 D1.
//
//  There are eight wire blocks in this repo — four server cost helpers and
//  four client ones — and they are supposed to be numeric mirrors. This
//  function is the whole rule. Call sites decide only where to put the cost
//  and the warning, never what either one is.
// ═══════════════════════════════════════════════════

/** What costing needs off one Hardware Library row. */
export interface WireHardwareEntry {
  cost_per_unit: number;
  name: string;
  unit?: string;
}

export interface WireHardwareResolution {
  /** The id actually used: the design's own, else the settings default, else null. */
  hardware_id: string | null;
  /** Null when nothing was found. Callers push a hardware_items row only when
   *  this is non-null — a row cannot be labelled without it. */
  name: string | null;
  cost_per_unit: number;
  cost: number;
  /** Non-null when wire is needed but could not be charged at a real rate. */
  warning: string | null;
}

const NOTHING: WireHardwareResolution = {
  hardware_id: null,
  name: null,
  cost_per_unit: 0,
  cost: 0,
  warning: null,
};

/** One decimal. The raw float is a division of inches by 12 and reads as noise. */
const fmtLf = (lf: number): string => String(Math.round(lf * 10) / 10);

/**
 * Resolve and price the tufting wire for one component.
 *
 * `lookup` is a callback rather than a Map for the same reason the button's is:
 * the two sides source their rates differently. The server's map is already
 * IVA- and currency-converted by each service's buildHardwareMap, while the
 * arm and cushion clients convert inline through their own hwConversions map —
 * and the client banquette helper converts nothing at all, matching
 * BanquetteCostService. Handing this function a Map would force one of them to
 * convert twice or not at all.
 *
 * **A negative `cost_per_unit` is treated as no rate, not as a credit.** The
 * `!(rate > 0)` test below catches it along with zero and NaN, so a wire
 * hardware entered at -$2 charges $0.00 with the "has no cost per unit"
 * warning rather than subtracting from the component. That is deliberate and
 * it is a price change on a path the spec's "What changes price, and what does
 * not" table did not originally name: before this function existed the old
 * `wireLf * hw.cost_per_unit` would have produced a negative line item. A
 * negative rate is a data-entry error in the Hardware Library, never a
 * discount someone modelled — a discount has no unit and no quantity to
 * multiply — and silently reducing a quote by it is the worse of the two
 * failures. resolveButtonHardware does the same, and the two must agree or the
 * pair stops being mirrorable.
 *
 * @param wireLinearFeet  from resolveWireLinearFeet, which already returns 0
 *                        for a design whose traits say it is not tufted and for
 *                        a zero channel count. Zero returns silently: the
 *                        default supplies hardware for wire that is needed, it
 *                        never creates the need. This is where the is_tufted
 *                        gate comes from — `channel_seam` has channels, earns
 *                        no wire, and must produce no warning. Do not add a
 *                        second trait test here.
 */
export function resolveWireHardware(
  wireLinearFeet: number,
  designWireHardwareId: string | null | undefined,
  defaultWireHardwareId: string | null | undefined,
  lookup: (id: string) => WireHardwareEntry | undefined,
): WireHardwareResolution {
  if (!(wireLinearFeet > 0)) return { ...NOTHING };

  // `||`, not `??`: a stored empty string is not a real id and must fall
  // through to the default exactly like null/undefined do.
  const id = designWireHardwareId || defaultWireHardwareId || null;
  if (!id) {
    return {
      ...NOTHING,
      warning:
        `${fmtLf(wireLinearFeet)} lin ft of tufting wire priced at $0.00 — this ` +
        `design names no wire hardware and no default is set in Configuration > Upholstery.`,
    };
  }

  const entry = lookup(id);
  if (!entry) {
    return {
      ...NOTHING,
      hardware_id: id,
      warning:
        `${fmtLf(wireLinearFeet)} lin ft of tufting wire priced at $0.00 — wire ` +
        `hardware ${id} was not found in the Hardware Library.`,
    };
  }

  const rate = Number.isFinite(entry.cost_per_unit) ? entry.cost_per_unit : 0;
  if (!(rate > 0)) {
    // Still named, so the caller pushes a visible $0.00 row — which is what it
    // did before this function existed. The warning is the new part.
    return {
      hardware_id: id,
      name: entry.name,
      cost_per_unit: 0,
      cost: 0,
      warning:
        `${fmtLf(wireLinearFeet)} lin ft of tufting wire priced at $0.00 — ` +
        `"${entry.name}" has no cost per unit in the Hardware Library.`,
    };
  }

  return {
    hardware_id: id,
    name: entry.name,
    cost_per_unit: rate,
    cost: wireLinearFeet * rate,
    warning: null,
  };
}
